import { Stack, Group, Button, Text, Paper, Center, Loader, Alert } from '@mantine/core';
import { IconDownload, IconPrinter, IconAlertCircle } from '@tabler/icons-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAssetToken } from '@/hooks/api/useAssetTokens';
import type { Asset } from '@/types/models';

interface AssetQRCodeProps {
  asset: Asset;
}

export function AssetQRCode({ asset }: AssetQRCodeProps) {
  const { data: tokenData, isLoading, error } = useAssetToken(asset.id);

  // Generate QR code URL
  const baseUrl = window.location.origin;
  const qrValue = tokenData
    ? `${baseUrl}/a/${tokenData.token}`
    : `${baseUrl}/assets?view=${asset.id}`; // Fallback to old format

  const handleDownload = () => {
    const svg = document.getElementById('asset-qr-code') as HTMLElement;
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `asset-${asset.tag_id}-qr.png`;
        link.click();
        URL.revokeObjectURL(url);
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const svg = document.getElementById('asset-qr-code') as HTMLElement;
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Asset QR Code - ${asset.tag_id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              page-break-inside: avoid;
            }
            .qr-code {
              margin: 20px 0;
            }
            .asset-info {
              margin-top: 10px;
            }
            .asset-tag {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .asset-description {
              font-size: 16px;
              color: #666;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="qr-code">${svgData}</div>
            <div class="asset-info">
              <div class="asset-tag">${asset.tag_id}</div>
              <div class="asset-description">${asset.description || ''}</div>
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (isLoading) {
    return (
      <Center p="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error loading QR code">
        Failed to load asset token. Please try again.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Paper p="xl" withBorder>
        <Center>
          <QRCodeSVG
            id="asset-qr-code"
            value={qrValue}
            size={256}
            level="H"
            includeMargin
          />
        </Center>
      </Paper>

      <Stack gap="sm">
        <Text size="sm" fw={500}>
          Asset Tag: {asset.tag_id}
        </Text>
        {tokenData && (
          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
            Token: {tokenData.token}
          </Text>
        )}
        <Text size="sm" c="dimmed">
          {asset.description}
        </Text>
        {asset.brand && asset.model && (
          <Text size="sm" c="dimmed">
            {asset.brand} - {asset.model}
          </Text>
        )}
      </Stack>

      <Text size="xs" c="dimmed">
        Scan this QR code with any QR scanner to quickly access asset details. Works from any
        device - login required.
      </Text>

      {tokenData && (
        <Text size="xs" c="dimmed">
          URL: {qrValue}
        </Text>
      )}

      <Group>
        <Button
          variant="light"
          leftSection={<IconDownload size={16} />}
          onClick={handleDownload}
          fullWidth
        >
          Download QR Code
        </Button>
        <Button
          variant="light"
          leftSection={<IconPrinter size={16} />}
          onClick={handlePrint}
          fullWidth
        >
          Print QR Code
        </Button>
      </Group>
    </Stack>
  );
}
